import { Box, Stack, Typography } from '@mui/material';
import CameraAltRounded from '@mui/icons-material/CameraAltRounded';
import FitnessCenterRounded from '@mui/icons-material/FitnessCenterRounded';
import HomeRounded from '@mui/icons-material/HomeRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import RestaurantRounded from '@mui/icons-material/RestaurantRounded';
import TimerRounded from '@mui/icons-material/TimerRounded';
import { transition } from '@/theme';
import { initials } from '@/utils/initials';
import { useAccentScheme } from '../format';

interface PhoneMockupProps {
  brandName: string;
  color: string;
  radius?: number;
  /** Tilt for hero compositions. */
  tilt?: number;
}

const BARS = [42, 58, 51, 66, 72, 69, 84];

/**
 * The student's app, drawn with the palette the product derives from a brand
 * color. Illustrative content; the colors and contrast rules are the real ones.
 */
export function PhoneMockup({ brandName, color, radius = 16, tilt = 0 }: PhoneMockupProps) {
  const s = useAccentScheme(color);
  const r = (factor = 1) => `${Math.round(radius * factor)}px`;
  const name = brandName.trim() || 'Sua marca';
  const ease = transition(['background-color', 'color', 'border-radius', 'border-color'], 'long2');

  return (
    <Box
      role="img"
      aria-label={`Prévia do app do aluno com a marca ${name}`}
      sx={{
        width: 300,
        maxWidth: '100%',
        aspectRatio: '300 / 610',
        mx: 'auto',
        p: '10px',
        borderRadius: '46px',
        background: 'linear-gradient(145deg, #2b2b35, #0d0d12)',
        boxShadow: `0 50px 90px -40px ${s.primary.dark}, 0 0 0 1px rgba(255,255,255,0.06) inset`,
        transform: tilt ? `rotate(${tilt}deg)` : undefined,
        transition: transition('box-shadow', 'long2'),
      }}
    >
      <Box
        sx={{
          height: '100%',
          borderRadius: '37px',
          overflow: 'hidden',
          bgcolor: s.background.default,
          color: s.text.primary,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontSize: 13,
          transition: ease,
        }}
      >
        {/* Status bar + notch */}
        <Stack
          direction="row"
          sx={{
            px: 3,
            pt: 1.25,
            pb: 0.5,
            justifyContent: 'space-between',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          <span>9:41</span>
          <Box sx={{ width: 84, height: 22, borderRadius: 99, bgcolor: '#0d0d12', mt: -0.25 }} />
          <span>100%</span>
        </Stack>

        {/* App bar with the brand */}
        <Stack direction="row" spacing={1.25} sx={{ px: 2, py: 1.25, alignItems: 'center' }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: r(0.7),
              display: 'grid',
              placeItems: 'center',
              bgcolor: s.primary.main,
              color: s.primary.contrastText,
              fontWeight: 800,
              fontSize: 13,
              transition: ease,
            }}
          >
            {initials(name)}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.1 }} noWrap>
            {name}
          </Typography>
        </Stack>

        <Stack spacing={1.25} sx={{ px: 1.75, flex: 1, overflow: 'hidden' }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700, px: 0.25, pt: 0.5 }}>
            Olá, Ana!
          </Typography>

          {/* Today's workout */}
          <Box
            sx={{
              p: 1.75,
              borderRadius: r(1.2),
              bgcolor: s.primary.container,
              color: s.primary.onContainer,
              transition: ease,
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <FitnessCenterRounded sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em' }}>
                TREINO DE HOJE
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 17, fontWeight: 800, mt: 0.5 }}>Inferiores A</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>6 exercícios · 50 min</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1.25, alignItems: 'center' }}>
              <Box
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 0.9,
                  borderRadius: r(0.8),
                  bgcolor: s.primary.main,
                  color: s.primary.contrastText,
                  fontWeight: 700,
                  transition: ease,
                }}
              >
                Começar treino
              </Box>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                  px: 1,
                  py: 0.75,
                  borderRadius: r(0.8),
                  border: `1.5px solid ${s.primary.main}`,
                  fontWeight: 700,
                  fontSize: 12,
                  transition: ease,
                }}
              >
                <TimerRounded sx={{ fontSize: 15 }} />
                <span>1:30</span>
              </Stack>
            </Stack>
          </Box>

          {/* Progress */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: r(1.2),
              bgcolor: s.background.paper,
              border: `1px solid ${s.divider}`,
              transition: ease,
            }}
          >
            <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>Agachamento (1RM)</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 800, color: s.success.main }}>
                +12%
              </Typography>
            </Stack>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{ alignItems: 'flex-end', height: 54, mt: 1 }}
            >
              {BARS.map((h, i) => (
                <Box
                  key={i}
                  sx={{
                    flex: 1,
                    height: `${h}%`,
                    borderRadius: '6px 6px 2px 2px',
                    bgcolor: i === BARS.length - 1 ? s.primary.main : s.primary.container,
                    transition: `${ease}, height 600ms`,
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Meal photo read by AI */}
          <Stack
            direction="row"
            spacing={1.25}
            sx={{
              p: 1.25,
              borderRadius: r(1.2),
              bgcolor: s.background.paper,
              border: `1px solid ${s.divider}`,
              alignItems: 'center',
              transition: ease,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: r(0.7),
                bgcolor: s.secondary.container,
                color: s.secondary.onContainer,
                display: 'grid',
                placeItems: 'center',
                transition: ease,
              }}
            >
              <CameraAltRounded sx={{ fontSize: 20 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                Almoço analisado pela IA
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: s.text.secondary }} noWrap>
                Arroz, feijão, frango · 540 kcal
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Bottom navigation */}
        <Stack
          direction="row"
          sx={{
            mt: 1,
            px: 1,
            pt: 1,
            pb: 2,
            justifyContent: 'space-around',
            bgcolor: s.background.paper,
            borderTop: `1px solid ${s.divider}`,
            transition: ease,
          }}
        >
          {[HomeRounded, FitnessCenterRounded, RestaurantRounded, InsightsRounded].map(
            (Icon, i) => (
              <Box
                key={i}
                sx={{
                  px: 1.75,
                  py: 0.5,
                  borderRadius: 99,
                  bgcolor: i === 1 ? s.primary.container : 'transparent',
                  color: i === 1 ? s.primary.onContainer : s.text.secondary,
                  display: 'grid',
                  placeItems: 'center',
                  transition: ease,
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
              </Box>
            ),
          )}
        </Stack>
      </Box>
    </Box>
  );
}
